import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { productsAPI, ordersAPI, bankAccountsAPI, onlinePaymentAPI, creditCardSystemsAPI, accountBalanceAPI, warehousesAPI, discountSettingsAPI, adminSettingsAPI, priceListAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  Search, Package, ClipboardList, Wallet, CreditCard, Clock, Building2, 
  ShoppingCart, Plus, Minus, Trash2, LogOut, Menu, X, FileText, Download,
  Filter, User, ChevronRight, AlertTriangle, CheckCircle, Truck, FileSpreadsheet,
  ExternalLink, Copy, Lock, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY'
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const calculatePrice = (product, userType, discountSettings) => {
  const basePrice = product.base_price || product.basePrice || 0;
  const i1 = product.iskonto1 || 0;
  const i2 = product.iskonto2 || 0;
  const i3 = product.iskonto3 || 0;
  const i4 = product.iskonto4 || 0;
  const shippingCost = product.shipping_cost || 0;

  // İskonto mantığı:
  // 1. i1+i2+i3 doluysa → Bireysel: i2 fiyat, Toptancı: i3 fiyat
  // 2. i1+i4 doluysa → Bireysel: i1 fiyat, Toptancı: i4 fiyat (ana fiyattan)
  // 3. Sadece i1 doluysa → Bireysel: i1 fiyat, Toptancı: i1 fiyat

  let discountedPrice = basePrice;
  
  if (i1 && i2 && i3) {
    // i1, i2, i3 dolu
    if (userType === 'bireysel') {
      discountedPrice = basePrice * (1 - i1/100) * (1 - i2/100);
    } else {
      discountedPrice = basePrice * (1 - i1/100) * (1 - i3/100);
    }
  } else if (i1 && i4) {
    // i1 ve i4 dolu
    if (userType === 'bireysel') {
      discountedPrice = basePrice * (1 - i1/100);
    } else {
      // Toptancı için i4 ana fiyattan hesaplanır (admin panelindeki gibi)
      discountedPrice = basePrice * (1 - i4/100);
    }
  } else if (i1) {
    // Sadece i1 dolu - hem bireysel hem toptancı için i1 fiyatı
    discountedPrice = basePrice * (1 - i1/100);
  }
  
  // Kargo ücreti fiyata eklenir
  return discountedPrice + shippingCost;
};

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const { cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotal, getItemCount } = useCart();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('urunler');
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [orderDateFilter, setOrderDateFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  // Görsel ayarları
  const [showImages, setShowImages] = useState(true);

  // Genel Liste Fiyatları (sadece toptancı için)
  const [priceList, setPriceList] = useState([]);
  const [priceListSearch, setPriceListSearch] = useState('');

  // API Data States
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [creditCardSystems, setCreditCardSystems] = useState([]);
  const [onlinePayment, setOnlinePayment] = useState({ link: '', password: '', description: '', is_active: false });
  const [accountBalance, setAccountBalance] = useState({ balance: 0, credit_limit: 50000, overdue_amount: 0 });
  const [transactions, setTransactions] = useState([]);
  const [discountSettings, setDiscountSettings] = useState({ iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 });

  // Load data from API
  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const [productsRes, ordersRes, warehousesRes, bankRes, ccRes, opRes, balanceRes, transRes, discRes, settingsRes] = await Promise.all([
        productsAPI.getAll().catch(() => ({ data: [] })),
        ordersAPI.getAll(user.id).catch(() => ({ data: [] })),
        warehousesAPI.getAll().catch(() => ({ data: [] })),
        bankAccountsAPI.getAll().catch(() => ({ data: [] })),
        creditCardSystemsAPI.getAll().catch(() => ({ data: [] })),
        onlinePaymentAPI.get().catch(() => ({ data: { link: '', password: '', description: '', is_active: false } })),
        accountBalanceAPI.get(user.id).catch(() => ({ data: { balance: 0, credit_limit: 50000, overdue_amount: 0 } })),
        accountBalanceAPI.getTransactions(user.id).catch(() => ({ data: [] })),
        discountSettingsAPI.get().catch(() => ({ data: { iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 } })),
        adminSettingsAPI.getPublic().catch(() => ({ data: { showImagesBireysel: true, showImagesToptanci: true } }))
      ]);
      
      setProducts(productsRes.data || []);
      setOrders(ordersRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setBankAccounts(bankRes.data || []);
      setCreditCardSystems(ccRes.data || []);
      setOnlinePayment(opRes.data || { link: '', password: '', description: '', is_active: false });
      setAccountBalance(balanceRes.data || { balance: 0, credit_limit: 50000, overdue_amount: 0 });
      setTransactions(transRes.data || []);
      setDiscountSettings(discRes.data || { iskonto1: 10, iskonto2: 5, iskonto3: 8, iskonto4: 12 });
      
      // Görsel ayarlarını kullanıcı tipine göre ayarla
      const publicSettings = settingsRes.data || {};
      if (user.role === 'bireysel') {
        setShowImages(publicSettings.showImagesBireysel !== false);
      } else if (user.role === 'toptanci') {
        setShowImages(publicSettings.showImagesToptanci !== false);
      } else {
        setShowImages(true);
      }
      
      // Toptancı ise Genel Liste Fiyatlarını yükle
      if (user.role === 'toptanci') {
        try {
          const priceListRes = await priceListAPI.getAllToptanci();
          setPriceList(priceListRes.data || []);
        } catch (error) {
          console.error('Error loading price list:', error);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id, loadData]);

  // Filtered data
  const userOrders = useMemo(() => orders.filter(o => o.user_id === user?.id), [orders, user?.id]);
  const activeBankAccounts = useMemo(() => bankAccounts.filter(b => b.is_active), [bankAccounts]);
  const activeCreditCardSystems = useMemo(() => creditCardSystems.filter(c => c.is_active), [creditCardSystems]);

  // Advanced search - handles various formats like "205/55", "205 55 primacy", "205 91v michelin"
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Görünürlük kontrolü - kullanıcı tipine göre
      // Eğer visible_bireysel true değilse (false veya undefined), bireysel kullanıcıya gösterme
      if (user?.role === 'bireysel' && product.visible_bireysel !== true) {
        return false;
      }
      // Eğer visible_toptanci true değilse (false veya undefined), toptancıya gösterme
      if (user?.role === 'toptanci' && product.visible_toptanci !== true) {
        return false;
      }
      
      // Depo bazlı görünürlük kontrolü
      if (user?.role === 'bireysel' && product.depot_bireysel && product.depot_bireysel !== 'all') {
        // Bireysel kullanıcı için sadece belirtilen depodaki stok varsa göster
        const depotStock = product.stock?.[product.depot_bireysel] || 0;
        if (depotStock <= 0) {
          return false;
        }
      }
      if (user?.role === 'toptanci' && product.depot_toptanci && product.depot_toptanci !== 'all') {
        // Toptancı kullanıcı için sadece belirtilen depodaki stok varsa göster
        const depotStock = product.stock?.[product.depot_toptanci] || 0;
        if (depotStock <= 0) {
          return false;
        }
      }
      
      // Normalize search term - remove special characters and split into tokens
      const searchLower = searchTerm.toLowerCase().trim();
      const searchTokens = searchLower
        .replace(/[\/\-_,\.]/g, ' ')  // Replace special chars with space
        .split(/\s+/)  // Split by whitespace
        .filter(t => t.length > 0);  // Remove empty tokens
      
      // Create searchable text from product
      const productText = [
        product.name,
        product.code,
        product.brand,
        product.season,
        product.dot
      ].join(' ').toLowerCase().replace(/[\/\-_,\.]/g, ' ');
      
      // Check if ALL tokens are found in product text
      const matchesSearch = searchTokens.length === 0 || 
        searchTokens.every(token => productText.includes(token));
      
      const matchesBrand = brandFilter === 'all' || product.brand === brandFilter;
      const matchesSeason = seasonFilter === 'all' || product.season === seasonFilter;
      
      return matchesSearch && matchesBrand && matchesSeason;
    });
  }, [products, searchTerm, brandFilter, seasonFilter, user?.role]);

  // Görseller kapalıysa ürünleri isim sırasına göre sırala
  const sortedFilteredProducts = useMemo(() => {
    if (!showImages) {
      // Görseller kapalıysa isim sırasına göre sırala
      return [...filteredProducts].sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      });
    }
    return filteredProducts;
  }, [filteredProducts, showImages]);

  // Pagination hesaplamaları
  const totalPages = Math.ceil(sortedFilteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    if (!showImages) {
      // Görseller kapalıysa pagination uygula
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      return sortedFilteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }
    return sortedFilteredProducts;
  }, [sortedFilteredProducts, currentPage, showImages]);

  // Sayfa değiştiğinde veya filtre değiştiğinde sayfa 1'e dön
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, brandFilter, seasonFilter]);

  const filteredOrders = useMemo(() => {
    if (!orderDateFilter) return userOrders;
    return userOrders.filter(o => o.date?.startsWith(orderDateFilter));
  }, [orderDateFilter, userOrders]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAddToCart = (product) => {
    if (!selectedWarehouse) {
      alert('Lütfen bir depo seçin!');
      return;
    }
    const price = calculatePrice(product, user?.role, discountSettings);
    const warehouseData = warehouses.find(w => w.id === selectedWarehouse);
    const cartItem = {
      ...product,
      selectedWarehouse,
      warehouseName: warehouseData?.name || selectedWarehouse
    };
    addToCart(cartItem, quantity, price);
    setSelectedProduct(null);
    setQuantity(1);
    setSelectedWarehouse('');
  };

  const handleCreateOrder = async () => {
    if (cart.length === 0) return;
    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          unit_price: item.price,
          total: item.price * item.quantity
        })),
        total_amount: getTotal()
      };
      await ordersAPI.create(orderData, user.id, user.name, user.role);
      alert('Siparişiniz başarıyla oluşturuldu!');
      clearCart();
      setIsCartOpen(false);
      loadData(); // Refresh data
    } catch (error) {
      alert('Sipariş oluşturulamadı: ' + (error.response?.data?.detail || 'Bir hata oluştu'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Kopyalandı!');
  };

  // Filtrelenmiş fiyat listesi (toptancı için)
  const filteredPriceList = useMemo(() => {
    if (!priceListSearch.trim()) return priceList;
    
    const searchLower = priceListSearch.toLowerCase();
    const searchDigits = priceListSearch.replace(/[^0-9]/g, '');
    
    return priceList.filter(item => {
      const textMatch = 
        item.stok_kodu?.toLowerCase().includes(searchLower) ||
        item.urun_adi?.toLowerCase().includes(searchLower) ||
        item.jant_grubu?.toLowerCase().includes(searchLower);
      
      // Birleşik rakam araması
      const itemDigits = (item.urun_adi || '').replace(/[^0-9]/g, '');
      const digitMatch = searchDigits.length >= 5 && itemDigits.includes(searchDigits);
      
      return textMatch || digitMatch;
    });
  }, [priceList, priceListSearch]);

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'Hazırlanıyor', className: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Kargoda', className: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Teslim Edildi', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'İptal', className: 'bg-red-100 text-red-800' }
    };
    const s = statusMap[status] || statusMap.pending;
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
  const uniqueSeasons = [...new Set(products.map(p => p.season).filter(Boolean))];

  // Menü öğeleri - toptancı için "Genel Liste Fiyatları" eklenir
  const baseMenuItems = [
    { id: 'urunler', label: 'Ürünler', icon: Package },
    { id: 'siparis', label: 'Sipariş Takibi', icon: ClipboardList },
    { id: 'cari', label: 'Cari Hesap', icon: Wallet },
    { id: 'bakiye', label: 'Hesap Bakiyesi', icon: CreditCard },
    { id: 'vadesi', label: 'Vadesi Geçen', icon: Clock },
    { id: 'hesapno', label: 'Hesap Numaraları', icon: Building2 },
    { id: 'online', label: 'Online Ödeme', icon: ExternalLink },
    { id: 'kredikarti', label: 'Kredi Kartı Ödemeleri', icon: CreditCard },
  ];

  // Toptancı için Genel Liste Fiyatları menüsü ekle
  const menuItems = user?.role === 'toptanci' 
    ? [
        { id: 'urunler', label: 'Ürünler', icon: Package },
        { id: 'genellistefiyatlari', label: 'Genel Liste Fiyatları', icon: FileSpreadsheet },
        { id: 'siparis', label: 'Sipariş Takibi', icon: ClipboardList },
        { id: 'cari', label: 'Cari Hesap', icon: Wallet },
        { id: 'bakiye', label: 'Hesap Bakiyesi', icon: CreditCard },
        { id: 'vadesi', label: 'Vadesi Geçen', icon: Clock },
        { id: 'hesapno', label: 'Hesap Numaraları', icon: Building2 },
        { id: 'online', label: 'Online Ödeme', icon: ExternalLink },
        { id: 'kredikarti', label: 'Kredi Kartı Ödemeleri', icon: CreditCard },
      ]
    : baseMenuItems;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navbar */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 mr-2"
              >
                <Menu className="w-6 h-6" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <svg width="24" height="24" viewBox="0 0 100 100" className="text-white">
                    <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="3" fill="none"/>
                    <circle cx="50" cy="50" r="8" fill="currentColor"/>
                  </svg>
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg font-semibold text-gray-900">Türkiye&apos;nin Lastik Havuzu</h1>
                  <p className="text-xs text-gray-500">B2B Sipariş Sistemi</p>
                </div>
              </div>
            </div>

            {/* Global Search */}
            <div className="flex-1 max-w-xl mx-4 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="205/55, 205 55 primacy, michelin 91v..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                />
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ShoppingCart className="w-6 h-6" />
                {getItemCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {getItemCount()}
                  </span>
                )}
              </button>
              <div className="hidden sm:flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.role === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout}>
                  <LogOut className="w-5 h-5 text-gray-600" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-gray-50"
            />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:shadow-none lg:border-r
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          pt-16 lg:pt-0
        `}>
          <div className="lg:hidden absolute top-4 right-4">
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 rounded-md hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-4 border-b lg:mt-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{user?.name}</p>
                <Badge variant="secondary" className="mt-1">
                  {user?.role === 'toptanci' ? 'Toptancı' : 'Bireysel'}
                </Badge>
              </div>
            </div>
          </div>

          <nav className="p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all
                  ${activeTab === item.id 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'}
                `}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
                {activeTab === item.id && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white lg:hidden">
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </aside>

        {/* Overlay for mobile sidebar */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-4rem)]">
          {/* Ürünler Tab */}
          {activeTab === 'urunler' && (
            <div className="space-y-4">
              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center gap-2 flex-1">
                      <Filter className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">Filtrele:</span>
                    </div>
                    <Select value={brandFilter} onValueChange={setBrandFilter}>
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Marka" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Markalar</SelectItem>
                        {uniqueBrands.map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Mevsim" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tüm Mevsimler</SelectItem>
                        {uniqueSeasons.map(season => (
                          <SelectItem key={season} value={season}>{season}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Products Grid */}
              {!showImages && (
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Toplam {sortedFilteredProducts.length} ürün ({currentPage}/{totalPages} sayfa)
                  </p>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Önceki
                      </Button>
                      <span className="text-sm px-2">{currentPage} / {totalPages}</span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Sonraki
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {showImages ? (
                // Görselli görünüm - Grid
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {paginatedProducts.map(product => {
                    const price = calculatePrice(product, user?.role, discountSettings);
                    
                    // Kullanıcı tipine göre stok hesaplama
                    let displayStock;
                    if (user?.role === 'bireysel' && product.depot_bireysel && product.depot_bireysel !== 'all') {
                      displayStock = product.stock?.[product.depot_bireysel] || 0;
                    } else if (user?.role === 'toptanci' && product.depot_toptanci && product.depot_toptanci !== 'all') {
                      displayStock = product.stock?.[product.depot_toptanci] || 0;
                    } else {
                      displayStock = Object.values(product.stock || {}).reduce((a, b) => a + b, 0);
                    }
                    
                    return (
                      <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="aspect-square bg-gray-100 relative p-4">
                          <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/200?text=Lastik';
                            }}
                          />
                          <Badge className="absolute top-2 right-2 bg-gray-800">{product.season}</Badge>
                        </div>
                        <CardContent className="p-4">
                          <p className="text-xs text-gray-500 mb-1">{product.code}</p>
                          <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">{product.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{product.brand}</p>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-lg font-bold text-blue-600">{formatCurrency(price)}</span>
                            <span className="text-xs text-gray-500">DOT: {product.dot}</span>
                          </div>
                          <div className="text-xs text-gray-500 mb-3">
                            Stok: <span className="font-medium text-gray-700">{displayStock} adet</span>
                          </div>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                onClick={() => {
                                  setSelectedProduct(product);
                                  setSelectedWarehouse('');
                                }}
                              >
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                Sepete Ekle
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Ürünü Sepete Ekle</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="flex items-center space-x-4">
                                  <img 
                                    src={product.image} 
                                    alt={product.name}
                                    className="w-20 h-20 object-contain bg-gray-100 rounded"
                                    onError={(e) => {
                                      e.target.src = 'https://via.placeholder.com/80?text=Lastik';
                                    }}
                                  />
                                  <div>
                                    <h4 className="font-medium">{product.name}</h4>
                                    <p className="text-sm text-gray-500">{product.brand}</p>
                                    <p className="text-lg font-bold text-blue-600 mt-1">{formatCurrency(price)}</p>
                                  </div>
                                </div>
                                
                                <div className="space-y-3">
                                  <p className="text-sm font-medium">Depo Seçin:</p>
                                  <RadioGroup value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                                    {warehouses.filter(wh => {
                                      // Kullanıcı tipine göre depo filtresi
                                    if (user?.role === 'bireysel' && product.depot_bireysel && product.depot_bireysel !== 'all') {
                                      return wh.id === product.depot_bireysel;
                                    }
                                    if (user?.role === 'toptanci' && product.depot_toptanci && product.depot_toptanci !== 'all') {
                                      return wh.id === product.depot_toptanci;
                                    }
                                    return true; // Tüm depolar gösterilir
                                  }).map(wh => {
                                    const stockCount = product.stock?.[wh.id] || 0;
                                    return (
                                      <div key={wh.id} className={`flex items-center justify-between p-3 border rounded-lg ${stockCount === 0 ? 'opacity-50' : 'hover:bg-gray-50'}`}>
                                        <div className="flex items-center space-x-3">
                                          <RadioGroupItem 
                                            value={wh.id} 
                                            id={`wh-${wh.id}`}
                                            disabled={stockCount === 0}
                                          />
                                          <Label htmlFor={`wh-${wh.id}`} className="cursor-pointer">
                                            {wh.name}
                                          </Label>
                                        </div>
                                        <Badge variant={stockCount > 0 ? 'default' : 'secondary'}>
                                          {stockCount} adet
                                        </Badge>
                                      </div>
                                    );
                                  })}
                                </RadioGroup>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className="font-medium">Adet:</span>
                                <div className="flex items-center space-x-3">
                                  <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <span className="w-12 text-center font-medium">{quantity}</span>
                                  <Button 
                                    variant="outline" 
                                    size="icon"
                                    onClick={() => setQuantity(quantity + 1)}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>

                              <div className="flex justify-between pt-4 border-t">
                                <span className="font-medium">Toplam:</span>
                                <span className="text-xl font-bold text-blue-600">
                                  {formatCurrency(price * quantity)}
                                </span>
                              </div>

                              <Button 
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                onClick={() => handleAddToCart(product)}
                                disabled={!selectedWarehouse}
                              >
                                {selectedWarehouse ? 'Sepete Ekle' : 'Önce Depo Seçin'}
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              ) : (
                // Görselsiz görünüm - Tablo (isim sıralı, sayfalı)
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün Kodu</TableHead>
                          <TableHead>Ürün Adı</TableHead>
                          <TableHead>Marka</TableHead>
                          <TableHead>Mevsim</TableHead>
                          <TableHead>DOT</TableHead>
                          <TableHead>Fiyat</TableHead>
                          <TableHead>Stok</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedProducts.map(product => {
                          const price = calculatePrice(product, user?.role, discountSettings);
                          let displayStock;
                          if (user?.role === 'bireysel' && product.depot_bireysel && product.depot_bireysel !== 'all') {
                            displayStock = product.stock?.[product.depot_bireysel] || 0;
                          } else if (user?.role === 'toptanci' && product.depot_toptanci && product.depot_toptanci !== 'all') {
                            displayStock = product.stock?.[product.depot_toptanci] || 0;
                          } else {
                            displayStock = Object.values(product.stock || {}).reduce((a, b) => a + b, 0);
                          }
                          
                          return (
                            <TableRow key={product.id}>
                              <TableCell className="font-mono text-sm">{product.code}</TableCell>
                              <TableCell className="font-medium">{product.name}</TableCell>
                              <TableCell>{product.brand}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{product.season}</Badge>
                              </TableCell>
                              <TableCell>{product.dot}</TableCell>
                              <TableCell className="font-bold text-blue-600">{formatCurrency(price)}</TableCell>
                              <TableCell>{displayStock} adet</TableCell>
                              <TableCell>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button 
                                      size="sm"
                                      className="bg-blue-600 hover:bg-blue-700"
                                      onClick={() => {
                                        setSelectedProduct(product);
                                        setSelectedWarehouse('');
                                      }}
                                    >
                                      <ShoppingCart className="w-3 h-3 mr-1" />
                                      Ekle
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="sm:max-w-md">
                                    <DialogHeader>
                                      <DialogTitle>Ürünü Sepete Ekle</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div>
                                        <h4 className="font-medium">{product.name}</h4>
                                        <p className="text-sm text-gray-500">{product.brand} | {product.code}</p>
                                        <p className="text-lg font-bold text-blue-600 mt-1">{formatCurrency(price)}</p>
                                      </div>
                                      
                                      <div className="space-y-3">
                                        <p className="text-sm font-medium">Depo Seçin:</p>
                                        <RadioGroup value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                                          {warehouses.filter(wh => {
                                            if (user?.role === 'bireysel' && product.depot_bireysel && product.depot_bireysel !== 'all') {
                                              return wh.id === product.depot_bireysel;
                                            }
                                            if (user?.role === 'toptanci' && product.depot_toptanci && product.depot_toptanci !== 'all') {
                                              return wh.id === product.depot_toptanci;
                                            }
                                            return true;
                                          }).map(wh => {
                                            const stock = product.stock?.[wh.id] || 0;
                                            return (
                                              <div key={wh.id} className="flex items-center space-x-2">
                                                <RadioGroupItem value={wh.id} id={`wh-tbl-${wh.id}-${product.id}`} disabled={stock === 0} />
                                                <Label htmlFor={`wh-tbl-${wh.id}-${product.id}`} className={stock === 0 ? 'text-gray-400' : ''}>
                                                  {wh.name} ({stock} adet)
                                                </Label>
                                              </div>
                                            );
                                          })}
                                        </RadioGroup>
                                      </div>

                                      <div className="space-y-2">
                                        <p className="text-sm font-medium">Adet:</p>
                                        <div className="flex items-center gap-3">
                                          <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                          >
                                            <Minus className="w-4 h-4" />
                                          </Button>
                                          <span className="w-12 text-center font-medium">{quantity}</span>
                                          <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => setQuantity(quantity + 1)}
                                          >
                                            <Plus className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>

                                      <div className="flex justify-between pt-4 border-t">
                                        <span className="font-medium">Toplam:</span>
                                        <span className="text-xl font-bold text-blue-600">
                                          {formatCurrency(price * quantity)}
                                        </span>
                                      </div>

                                      <Button 
                                        className="w-full bg-blue-600 hover:bg-blue-700"
                                        onClick={() => handleAddToCart(product)}
                                        disabled={!selectedWarehouse}
                                      >
                                        {selectedWarehouse ? 'Sepete Ekle' : 'Önce Depo Seçin'}
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Alt Pagination */}
              {!showImages && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    İlk
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Önceki
                  </Button>
                  <span className="text-sm px-4">Sayfa {currentPage} / {totalPages}</span>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Sonraki
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Son
                  </Button>
                </div>
              )}

              {sortedFilteredProducts.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aranan kriterlere uygun ürün bulunamadı.</p>
                </div>
              )}
            </div>
          )}

          {/* Genel Liste Fiyatları - Sadece Toptancı */}
          {activeTab === 'genellistefiyatlari' && user?.role === 'toptanci' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Genel Liste Fiyatları</h2>
                <Badge variant="outline" className="text-sm">
                  {priceList.length} Kayıt
                </Badge>
              </div>

              {/* Arama */}
              <Card>
                <CardContent className="p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      placeholder="Ürün ara (stok kodu, ürün adı, jant grubu, 2055516 gibi)..."
                      value={priceListSearch}
                      onChange={(e) => setPriceListSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {priceListSearch && (
                    <p className="text-sm text-gray-500 mt-2">
                      {filteredPriceList.length} / {priceList.length} kayıt gösteriliyor
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Veri Tablosu */}
              {priceList.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Henüz liste fiyatı yüklenmemiş.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[600px]">
                      <Table>
                        <TableHeader className="sticky top-0 bg-gray-100 z-10">
                          <TableRow>
                            <TableHead className="font-bold">Stok Kodu</TableHead>
                            <TableHead className="font-bold">Ürün Adı</TableHead>
                            <TableHead className="font-bold text-right">Liste Fiyatı</TableHead>
                            <TableHead className="font-bold text-right">Perakende Fiyatı</TableHead>
                            <TableHead className="font-bold text-right">Toptan Fiyatı</TableHead>
                            <TableHead className="font-bold">Jant Grubu</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredPriceList.map((item, index) => (
                            <TableRow key={index} className="hover:bg-gray-50">
                              <TableCell className="font-mono text-sm">{item.stok_kodu}</TableCell>
                              <TableCell>{item.urun_adi}</TableCell>
                              <TableCell className="text-right font-medium">{formatCurrency(item.liste_fiyati)}</TableCell>
                              <TableCell className="text-right font-medium text-blue-600">{formatCurrency(item.perakende_fiyati)}</TableCell>
                              <TableCell className="text-right font-medium text-green-600">{formatCurrency(item.toptan_fiyati)}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{item.jant_grubu}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Sipariş Takibi */}
          {activeTab === 'siparis' && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <CardTitle className="text-lg">Siparişlerim</CardTitle>
                    <div className="flex gap-2">
                      <Input
                        type="date"
                        value={orderDateFilter}
                        onChange={(e) => setOrderDateFilter(e.target.value)}
                        className="w-auto"
                      />
                      <Button variant="outline" size="sm">
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Excel
                      </Button>
                      <Button variant="outline" size="sm">
                        <FileText className="w-4 h-4 mr-2" />
                        PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="w-full">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sipariş No</TableHead>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Tutar</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead>Teslim Tarihi</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredOrders.map(order => (
                          <TableRow key={order.id}>
                            <TableCell className="font-medium">{order.id}</TableCell>
                            <TableCell>{formatDate(order.date)}</TableCell>
                            <TableCell>{formatCurrency(order.totalAmount)}</TableCell>
                            <TableCell>{getStatusBadge(order.status)}</TableCell>
                            <TableCell>{order.deliveryDate ? formatDate(order.deliveryDate) : '-'}</TableCell>
                            <TableCell>
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button variant="ghost" size="sm">Detay</Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl">
                                  <DialogHeader>
                                    <DialogTitle>Sipariş Detayı - {order.id}</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <p className="text-sm text-gray-500">Sipariş Tarihi</p>
                                        <p className="font-medium">{formatDate(order.date)}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-500">Durum</p>
                                        {getStatusBadge(order.status)}
                                      </div>
                                    </div>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Ürün</TableHead>
                                          <TableHead>Adet</TableHead>
                                          <TableHead>Birim Fiyat</TableHead>
                                          <TableHead>Toplam</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {order.items.map((item, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{item.productName}</TableCell>
                                            <TableCell>{item.quantity}</TableCell>
                                            <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                                            <TableCell>{formatCurrency(item.total)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                    <div className="text-right border-t pt-4">
                                      <p className="text-lg font-bold">Toplam: {formatCurrency(order.totalAmount)}</p>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  {filteredOrders.length === 0 && (
                    <div className="text-center py-8">
                      <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">Henüz siparişiniz bulunmuyor.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Cari Hesap */}
          {activeTab === 'cari' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Cari Bakiye</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(accountBalance.balance)}</p>
                      </div>
                      <Wallet className="w-10 h-10 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Kredi Limiti</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(accountBalance.credit_limit)}</p>
                      </div>
                      <CreditCard className="w-10 h-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Kullanılabilir</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(accountBalance.credit_limit - accountBalance.balance)}
                        </p>
                      </div>
                      <CheckCircle className="w-10 h-10 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Hesap Hareketleri</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Tutar</TableHead>
                        <TableHead>Durum</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map(tx => (
                        <TableRow key={tx.id}>
                          <TableCell>{formatDate(tx.date)}</TableCell>
                          <TableCell>{tx.description}</TableCell>
                          <TableCell className={tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                          </TableCell>
                          <TableCell>
                            <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'}>
                              {tx.status === 'completed' ? 'Tamamlandı' : 'Beklemede'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Hesap Bakiyesi */}
          {activeTab === 'bakiye' && (
            <Card>
              <CardHeader>
                <CardTitle>Hesap Bakiyesi</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">Mevcut Bakiye</p>
                      <p className="text-3xl font-bold text-blue-700">{formatCurrency(accountBalance.balance)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Son Ödeme Tarihi</p>
                      <p className="text-xl font-semibold">{formatDate(accountBalance.last_payment_date)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Son Ödeme Tutarı</p>
                      <p className="text-xl font-semibold">{formatCurrency(accountBalance.last_payment_amount || 0)}</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-6 text-white">
                    <h3 className="text-lg font-medium mb-4">Kredi Limiti Kullanımı</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Toplam Limit:</span>
                        <span className="font-bold">{formatCurrency(accountBalance.credit_limit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Kullanılan:</span>
                        <span className="font-bold">{formatCurrency(accountBalance.balance)}</span>
                      </div>
                      <div className="h-2 bg-blue-900 rounded-full mt-4">
                        <div 
                          className="h-full bg-white rounded-full"
                          style={{ width: `${Math.min((accountBalance.balance / (accountBalance.credit_limit || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-sm opacity-80 mt-2">
                        Kullanılabilir: {formatCurrency(accountBalance.credit_limit - accountBalance.balance)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vadesi Geçen */}
          {activeTab === 'vadesi' && (
            <Card>
              <CardHeader>
                <CardTitle>Vadesi Geçen Borçlar</CardTitle>
              </CardHeader>
              <CardContent>
                {accountBalance.overdue_amount > 0 ? (
                  <div className="space-y-4">
                    <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <AlertTriangle className="w-12 h-12 text-red-500" />
                        <div>
                          <p className="text-sm text-red-600">Vadesi Geçen Tutar</p>
                          <p className="text-3xl font-bold text-red-700">{formatCurrency(accountBalance.overdue_amount)}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Lütfen vadesi geçen borcunuzu en kısa sürede ödeyiniz. 
                      Sorularınız için müşteri hizmetleri ile iletişime geçebilirsiniz.
                    </p>
                    <Button className="bg-red-600 hover:bg-red-700">
                      Hemen Öde
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <p className="text-lg font-medium text-gray-900">Vadesi geçen borcunuz bulunmamaktadır.</p>
                    <p className="text-gray-500 mt-2">Tebrikler! Tüm ödemeleriniz güncel.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Hesap Numaraları */}
          {activeTab === 'hesapno' && (
            <Card>
              <CardHeader>
                <CardTitle>Banka Hesap Numaraları</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeBankAccounts.map(account => (
                    <Card key={account.id} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3 mb-4">
                          <Building2 className="w-10 h-10 text-blue-600" />
                          <div>
                            <h3 className="font-semibold">{account.bank_name}</h3>
                            <p className="text-xs text-gray-500">{account.branch}</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-gray-500">Hesap Adı</p>
                            <p className="text-sm font-medium">{account.account_name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">IBAN</p>
                            <p className="text-sm font-mono bg-gray-50 p-2 rounded">{account.iban}</p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          className="w-full mt-4" 
                          size="sm"
                          onClick={() => copyToClipboard(account.iban)}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          IBAN Kopyala
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {activeBankAccounts.length === 0 && (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Henüz hesap numarası tanımlanmamış.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Online Ödeme */}
          {activeTab === 'online' && (
            <Card>
              <CardHeader>
                <CardTitle>Online Ödeme</CardTitle>
              </CardHeader>
              <CardContent>
                {onlinePayment.is_active ? (
                  <div className="max-w-lg mx-auto space-y-6">
                    <p className="text-gray-600">{onlinePayment.description}</p>
                    
                    <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Ödeme Linki:</p>
                        <div className="flex items-center gap-2">
                          <Input value={onlinePayment.link} readOnly className="bg-white" />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => copyToClipboard(onlinePayment.link)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Şifre:</p>
                        <div className="flex items-center gap-2">
                          <Input value={onlinePayment.password} readOnly className="bg-white font-mono" />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => copyToClipboard(onlinePayment.password)}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => window.open(onlinePayment.link, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ödeme Sayfasına Git
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Lock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Online ödeme şu an aktif değil.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Kredi Kartı Ödemeleri */}
          {activeTab === 'kredikarti' && (
            <Card>
              <CardHeader>
                <CardTitle>Kredi Kartı ile Ödeme</CardTitle>
              </CardHeader>
              <CardContent>
                {activeCreditCardSystems.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-gray-600 mb-4">Aktif ödeme sistemleri:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {activeCreditCardSystems.map(system => (
                        <Card key={system.id} className="border-2">
                          <CardContent className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-xl font-semibold">{system.name}</h3>
                              <Badge className="bg-green-100 text-green-800">Aktif</Badge>
                            </div>
                            <Button className="w-full bg-blue-600 hover:bg-blue-700">
                              <CreditCard className="w-4 h-4 mr-2" />
                              {system.name} ile Öde
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Aktif kredi kartı ödeme sistemi bulunmuyor.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </main>
      </div>

      {/* Cart Drawer */}
      {isCartOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsCartOpen(false)} />
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Sepetim ({getItemCount()} ürün)</h2>
              <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <ScrollArea className="flex-1 p-4">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Sepetiniz boş</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={`${item.productId}-${item.selectedWarehouse}`} className="flex flex-col p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <img 
                          src={item.image} 
                          alt={item.productName}
                          className="w-16 h-16 object-contain bg-white rounded"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/64?text=Lastik';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.productName}</p>
                          <p className="text-xs text-gray-500">{item.brand}</p>
                          <p className="text-sm font-bold text-blue-600">{formatCurrency(item.unitPrice)}</p>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.productId)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t">
                        <Badge variant="outline" className="text-xs">
                          <Building2 className="w-3 h-3 mr-1" />
                          {item.warehouseName}
                        </Badge>
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {cart.length > 0 && (
              <div className="p-4 border-t space-y-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Toplam:</span>
                  <span className="text-blue-600">{formatCurrency(getTotal())}</span>
                </div>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                  onClick={handleCreateOrder}
                >
                  <Truck className="w-5 h-5 mr-2" />
                  Sipariş Oluştur
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={clearCart}
                >
                  Sepeti Temizle
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserDashboard;
